import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc, onSnapshot, collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { parseFlexible, formatTrDateOnly } from '../utils/hopsDate';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  LayoutDashboard,
  Users,
  Truck,
  ClipboardCheck,
  Building2,
  TrendingUp,
  TrendingDown,
  Calendar,
  FileDown,
  Table,
  RefreshCw,
  Search,
  LogOut,
  ChevronsUpDown,
  ChevronDown,
  ArrowUpDown,
  CheckCircle,
  CircleSlash,
  Bell,
  X,
  MapPin,
  UserCircle,
  Phone,
  Mail,
} from 'lucide-react';
import { app, database } from './firebaseConfig';
import './AdminAnalytics.css';

const DONUT_COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899'];
const FUNNEL_COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899'];
const CATEGORY_THEME = [
  { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)', chipBg: 'rgba(245, 158, 11, 0.12)' },
  { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)', chipBg: 'rgba(59, 130, 246, 0.12)' },
  { color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)', chipBg: 'rgba(16, 185, 129, 0.12)' },
  { color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.15)', chipBg: 'rgba(139, 92, 246, 0.12)' },
];
const FIRMA_PAGE_SIZE = 12;

function formatNum(n) {
  if (n == null || Number.isNaN(n)) return '0';
  return new Intl.NumberFormat('tr-TR').format(Math.round(n));
}
function formatCompact(n) {
  if (n == null) return '0';
  const num = Number(n);
  if (num >= 1e6) return `${(num / 1e6).toFixed(2).replace('.', ',')}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1).replace('.', ',')}K`;
  return formatNum(num);
}
function currentQuarterLabel() {
  const q = Math.floor(new Date().getMonth() / 3) + 1;
  return `Q${q} ${new Date().getFullYear()}`;
}
function pjDate(p) {
  if (!p) return null;
  const ts = p.timestamp;
  if (ts && typeof ts.toDate === 'function') {
    try { return ts.toDate(); } catch { /* noop */ }
  }
  return (
    parseFlexible(p.Tarih) ||
    parseFlexible(p.createdAt) ||
    parseFlexible(p.tarih) ||
    parseFlexible(p.date) ||
    null
  );
}

function formatTrDate(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '-';
  }
}
function maxOf(arr, key) {
  return Math.max(1, ...arr.map((x) => (key ? x[key] : x) || 0));
}
function statusLabel(status) {
  if (status === 'active') return { text: 'Aktif', cls: 'status-active' };
  return { text: 'Pasif', cls: 'status-idle' };
}
function buildDonutSegments(items) {
  const total = items.reduce((s, x) => s + (x.count || 0), 0) || 1;
  const circumference = 2 * Math.PI * 50;
  let offset = 52;
  return items.map((item, i) => {
    const ratio = (item.count || 0) / total;
    const dash = ratio * circumference;
    const seg = {
      color: DONUT_COLORS[i % DONUT_COLORS.length],
      dasharray: `${dash} ${circumference - dash}`,
      offset,
    };
    offset -= dash;
    return seg;
  });
}
// Tek bir değer alanı (key) için, paylaşılan bir max'e göre çizgi+alan yolu üretir.
function buildKeyPaths(series, key, max, w = 900, h = 120) {
  if (!series || series.length === 0) return { line: '', area: '', points: [] };
  const safeMax = Math.max(1, max);
  const n = series.length;
  const points = series.map((s, i) => {
    const x = n === 1 ? 0 : (i / (n - 1)) * w;
    const v = Number(s[key]) || 0;
    const y = h - (v / safeMax) * (h - 4) - 2;
    return { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10, value: v, label: s.label };
  });
  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const area = `${line} L${w},${h} L0,${h} Z`;
  return { line, area, points };
}
function exportFirmsCsv(firms) {
  const headers = ['Firma', 'E-posta', 'Yetkili', 'Telefon', 'Adres', 'Makine', 'Puantaj', 'Kayit', 'Durum'];
  const rows = firms.map((f) => [
    f.name, f.email, f.contact, f.phone, f.address,
    f.machineCount, f.puantajCount, f.createdAt ? f.createdAt.slice(0, 10) : '', f.status,
  ]);
  const csv = [headers, ...rows]
    .map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `hops-firmalar-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const AdminAnalytics = ({ userEmail, onSignOut }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [puantajTab, setPuantajTab] = useState('weekly');
  const [cumMode, setCumMode] = useState('puantaj'); // 'puantaj' | 'kullanici'
  const [cumHover, setCumHover] = useState(null); // hover edilen hafta index'i
  const [firmaSearch, setFirmaSearch] = useState('');
  const [firmaFilter, setFirmaFilter] = useState('all');
  const [firmaSort, setFirmaSort] = useState({ key: 'puantajCount', dir: 'desc' });
  const [firmaPage, setFirmaPage] = useState(1);

  const functions = useMemo(() => getFunctions(app, 'us-central1'), []);
  const recompute = useMemo(() => httpsCallable(functions, 'recomputePlatformStats'), [functions]);
  const sendBulk = useMemo(() => httpsCallable(functions, 'sendBulkNotification'), [functions]);
  const previewBulk = useMemo(() => httpsCallable(functions, 'previewBulkNotificationTargets'), [functions]);

  // Bildirim gönderme durumu
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifTitle, setNotifTitle] = useState('');
  const [notifBody, setNotifBody] = useState('');
  const [notifMode, setNotifMode] = useState('all'); // 'all' | 'selected'
  const [notifSelected, setNotifSelected] = useState([]);
  const [notifSearch, setNotifSearch] = useState('');
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifPreviewLoading, setNotifPreviewLoading] = useState(false);
  const [notifMsg, setNotifMsg] = useState(null);
  const [notifPreview, setNotifPreview] = useState(null);
  const [notifSuccess, setNotifSuccess] = useState(null);

  // Firma detay popup
  const [detailFirm, setDetailFirm] = useState(null);
  const [detailMachines, setDetailMachines] = useState(null);
  const [detailOperators, setDetailOperators] = useState(null);
  const [detailPuantajlar, setDetailPuantajlar] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    document.body.classList.add('hops-admin-active');
    document.documentElement.style.backgroundColor = '#0d0f14';
    return () => {
      document.body.classList.remove('hops-admin-active');
      document.documentElement.style.backgroundColor = '';
    };
  }, []);

  const loadStats = useCallback(async () => {
    const ref = doc(database, 'platformStats', 'global');
    const snap = await getDoc(ref);
    if (snap.exists()) {
      setStats(snap.data());
      setError('');
    } else {
      setStats(null);
      setError('Henüz istatistik hesaplanmamış. "Yenile" ile ilk hesaplamayı başlatın.');
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    const ref = doc(database, 'platformStats', 'global');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          setStats(snap.data());
          setError('');
        } else {
          setStats(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setError('İstatistikler okunamadı.');
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError('');
    try {
      await recompute();
      await loadStats();
    } catch (e) {
      console.error(e);
      setError(e.message || 'Yenileme başarısız.');
    } finally {
      setRefreshing(false);
    }
  };

  const toggleNotifEmail = (email) => {
    setNotifSelected((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
    setNotifPreview(null);
  };

  const handlePreviewNotif = async () => {
    setNotifPreviewLoading(true);
    setNotifMsg(null);
    try {
      const res = await previewBulk({
        sendToAll: notifMode === 'all',
        targetUserEmails: notifMode === 'selected' ? notifSelected : [],
      });
      setNotifPreview(res.data?.summary || null);
    } catch (e) {
      setNotifMsg({ type: 'error', text: 'Önizleme hatası: ' + (e.message || e) });
    } finally {
      setNotifPreviewLoading(false);
    }
  };

  const handleSendNotif = async () => {
    if (!notifTitle.trim() || !notifBody.trim()) {
      setNotifMsg({ type: 'error', text: 'Başlık ve mesaj zorunludur.' });
      return;
    }
    if (notifMode === 'selected' && notifSelected.length === 0) {
      setNotifMsg({ type: 'error', text: 'En az bir firma seçin.' });
      return;
    }
    setNotifLoading(true);
    setNotifMsg(null);
    try {
      const res = await sendBulk({
        title: notifTitle,
        body: notifBody,
        targetUserEmails: notifMode === 'selected' ? notifSelected : [],
        sendToAll: notifMode === 'all',
        customData: { type: 'admin_message' },
      });
      const s = res.data?.stats || {};
      setNotifSuccess({
        totalUsers: s.totalUsers ?? 0,
        totalTokens: s.totalTokens ?? 0,
        successCount: s.successCount ?? 0,
        failureCount: s.failureCount ?? 0,
      });
    } catch (e) {
      setNotifMsg({ type: 'error', text: 'Gönderim hatası: ' + (e.message || e) });
    } finally {
      setNotifLoading(false);
    }
  };

  const closeNotifSuccess = () => {
    setNotifSuccess(null);
    setNotifOpen(false);
    setNotifTitle('');
    setNotifBody('');
    setNotifSelected([]);
    setNotifSearch('');
    setNotifPreview(null);
    setNotifMsg(null);
  };

  const openFirmDetail = async (firm) => {
    setDetailFirm(firm);
    setDetailMachines(null);
    setDetailOperators(null);
    setDetailPuantajlar(null);
    setDetailLoading(true);

    const base = collection(database, 'users', firm.id, 'machines');

    // Makine envanteri (gruplu)
    const machinesP = getDocs(base)
      .then((snap) => {
        const groups = {};
        snap.forEach((d) => {
          const m = d.data() || {};
          const brand = String(m.Marka || m.marka || '').trim();
          const model = String(m.Model || m.model || '').trim();
          const type = String(m.MakineTürü || m.makineTuru || m.MakineTuru || m.type || 'Belirtilmemiş').trim() || 'Belirtilmemiş';
          const key = `${brand}|${model}|${type}`;
          if (!groups[key]) groups[key] = { brand, model, type, count: 0 };
          groups[key].count += 1;
        });
        setDetailMachines(Object.values(groups).sort((a, b) => b.count - a.count));
      })
      .catch(() => setDetailMachines([]));

    // Operatörler
    const opsP = getDocs(collection(database, 'users', firm.id, 'operators'))
      .then((snap) => {
        const ops = snap.docs.map((d) => {
          const o = d.data() || {};
          return {
            id: d.id,
            name: `${o.name || ''} ${o.surname || ''}`.trim() || o.operatorName || o.name || '-',
            phone: o.phoneNumberDisplay || o.phoneNumber || o.phone || '-',
          };
        });
        setDetailOperators(ops);
      })
      .catch(() => setDetailOperators([]));

    // Son 10 puantaj
    const pjP = (async () => {
      let docs = [];
      try {
        const qs = await getDocs(
          query(collection(database, 'users', firm.id, 'timeSheets'), orderBy('timestamp', 'desc'), limit(10))
        );
        docs = qs.docs.map((d) => d.data());
      } catch (e) {
        docs = [];
      }
      if (docs.length === 0) {
        try {
          const qs = await getDocs(query(collection(database, 'users', firm.id, 'timeSheets'), limit(60)));
          docs = qs.docs.map((d) => d.data());
        } catch (e) {
          docs = [];
        }
      }
      const withDate = docs.map((p) => ({ p, d: pjDate(p) }));
      withDate.sort((a, b) => (b.d?.getTime() || 0) - (a.d?.getTime() || 0));
      setDetailPuantajlar(withDate.slice(0, 10).map((x) => x.p));
    })();

    try {
      await Promise.all([machinesP, opsP, pjP]);
    } finally {
      setDetailLoading(false);
    }
  };

  const sendNotifToFirm = (firm) => {
    setNotifSelected([firm.email]);
    setNotifMode('selected');
    setNotifSearch('');
    setNotifPreview(null);
    setNotifMsg(null);
    setDetailFirm(null);
    setNotifOpen(true);
  };

  const notifFirms = useMemo(() => {
    const list = (stats?.allFirms || []).filter((f) => f.email);
    const q = notifSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (f) => (f.name || '').toLowerCase().includes(q) || (f.email || '').toLowerCase().includes(q)
    );
  }, [stats, notifSearch]);

  const totals = useMemo(() => stats?.totals || {}, [stats]);
  const trends = useMemo(() => stats?.trends || {}, [stats]);
  const monthlyGrowth = useMemo(() => stats?.monthlyGrowth || [], [stats]);
  const machineCategories = useMemo(() => stats?.machineCategories || [], [stats]);
  const machineCategoriesTotal = stats?.machineCategoriesTotal || totals.machineCount || 0;
  const machineModels = useMemo(() => stats?.machineModels || [], [stats]);
  const topModels = useMemo(() => stats?.topModels || [], [stats]);
  const roleDistribution = useMemo(() => stats?.roleDistribution || {}, [stats]);
  const activityApprox = useMemo(() => stats?.activityApprox || {}, [stats]);
  const dauApprox = useMemo(() => stats?.dauApprox || { series: [] }, [stats]);
  const puantajByModule = useMemo(() => stats?.puantajByModule || [], [stats]);
  const puantajTimeSeries = useMemo(() => stats?.puantajTimeSeries || {}, [stats]);
  const topFirms = useMemo(() => stats?.topFirms || [], [stats]);
  const allFirms = useMemo(() => stats?.allFirms || [], [stats]);
  const regions = useMemo(() => stats?.regions || [], [stats]);

  const growthMax = Math.max(
    maxOf(monthlyGrowth, 'puantaj'),
    maxOf(monthlyGrowth, 'firms'),
    maxOf(monthlyGrowth, 'active'),
    1
  );

  const donutSegments = useMemo(() => buildDonutSegments(machineCategories), [machineCategories]);

  const puantajSeries = useMemo(() => {
    if (puantajTab === 'daily') return puantajTimeSeries.daily || [];
    if (puantajTab === 'monthly') return puantajTimeSeries.monthly || [];
    return puantajTimeSeries.weekly || [];
  }, [puantajTab, puantajTimeSeries]);
  const puantajMax = maxOf(puantajSeries, 'count');
  const hasApproved = puantajSeries.some((p) => p.approved != null);

  const weekly = puantajTimeSeries.weekly || [];
  const thisWeek = weekly.length ? weekly[weekly.length - 1].count : 0;
  const lastWeek = weekly.length > 1 ? weekly[weekly.length - 2].count : 0;
  const weekChange = lastWeek ? Math.round(((thisWeek - lastWeek) / lastWeek) * 1000) / 10 : 0;

  // Haftalık kümülatif büyüme (2025 Haziran -> bugün)
  const growthWeekly = useMemo(() => stats?.growthWeekly || [], [stats]);
  const CHART_W = 900;
  const CHART_H = 150;
  const growthLast = growthWeekly.length ? growthWeekly[growthWeekly.length - 1] : null;
  const cumChart = useMemo(() => {
    if (cumMode === 'kullanici') {
      const max = Math.max(1, ...growthWeekly.map((d) => Number(d.users) || 0));
      return {
        max,
        primary: buildKeyPaths(growthWeekly, 'users', max, CHART_W, CHART_H),
        secondary: buildKeyPaths(growthWeekly, 'usersFiltered', max, CHART_W, CHART_H),
      };
    }
    const max = Math.max(1, ...growthWeekly.map((d) => Number(d.puantaj) || 0));
    return {
      max,
      primary: buildKeyPaths(growthWeekly, 'puantaj', max, CHART_W, CHART_H),
      secondary: buildKeyPaths(growthWeekly, 'puantajExcl', max, CHART_W, CHART_H),
    };
  }, [cumMode, growthWeekly]);
  const cumSecondaryColor = cumMode === 'kullanici' ? '#22c55e' : '#3b82f6';
  // X ekseninde kalabalık olmasın diye ~10 etiket göster.
  const growthTicks = useMemo(() => {
    const n = growthWeekly.length;
    if (!n) return [];
    const step = Math.max(1, Math.ceil(n / 10));
    const out = [];
    for (let i = 0; i < n; i += step) out.push({ i, label: growthWeekly[i].label });
    if (out.length && out[out.length - 1].i !== n - 1) {
      out.push({ i: n - 1, label: growthWeekly[n - 1].label });
    }
    return out;
  }, [growthWeekly]);

  const dauMax = maxOf(dauApprox.series || [], 'count');

  const filteredFirms = useMemo(() => {
    let list = [...allFirms];
    const q = firmaSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (f) =>
          (f.name || '').toLowerCase().includes(q) ||
          (f.email || '').toLowerCase().includes(q) ||
          (f.contact || '').toLowerCase().includes(q) ||
          (f.phone || '').includes(q)
      );
    }
    if (firmaFilter !== 'all') list = list.filter((f) => f.status === firmaFilter);
    const { key, dir } = firmaSort;
    list.sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (typeof av === 'string' || key === 'createdAt') {
        return dir === 'asc'
          ? String(av || '').localeCompare(String(bv || ''), 'tr')
          : String(bv || '').localeCompare(String(av || ''), 'tr');
      }
      return dir === 'asc' ? (av || 0) - (bv || 0) : (bv || 0) - (av || 0);
    });
    return list;
  }, [allFirms, firmaSearch, firmaFilter, firmaSort]);

  const firmaPageCount = Math.max(1, Math.ceil(filteredFirms.length / FIRMA_PAGE_SIZE));
  const pagedFirms = filteredFirms.slice((firmaPage - 1) * FIRMA_PAGE_SIZE, firmaPage * FIRMA_PAGE_SIZE);

  useEffect(() => { setFirmaPage(1); }, [firmaSearch, firmaFilter, firmaSort]);

  const setSort = (key, dir) => setFirmaSort({ key, dir });
  const toggleSort = (key) =>
    setFirmaSort((p) => (p.key === key ? { key, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));

  const TrendBadge = ({ value }) => {
    const v = Number(value) || 0;
    const up = v >= 0;
    return (
      <div className={`kpi-trend ${up ? 'up' : 'down'}`}>
        {up ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
        {up ? '+' : ''}{v}%
      </div>
    );
  };

  const sortLabel =
    firmaSort.key === 'createdAt' && firmaSort.dir === 'desc' ? 'Yeniden Eskiye'
    : firmaSort.key === 'createdAt' ? 'Eskiden Yeniye'
    : firmaSort.key === 'puantajCount' ? 'Puantaj Sayısı ↓'
    : firmaSort.key === 'machineCount' ? 'Makine Sayısı ↓'
    : firmaSort.key === 'name' && firmaSort.dir === 'asc' ? 'Alfabetik (A–Z)'
    : firmaSort.key === 'name' ? 'Alfabetik (Z–A)'
    : 'Sırala';

  if (loading && !stats) {
    return (
      <div className="hops-admin">
        <div className="loading-overlay">Platform istatistikleri yükleniyor…</div>
      </div>
    );
  }

  return (
    <div className="hops-admin">
      {/* TOP BAR */}
      <div id="topbar">
        <div id="topbar-brand">
          <div className="logo-mark">
            <LayoutDashboard size={16} color="#0d0f14" />
          </div>
          <div>
            <div className="logo-text">HOPS</div>
            <div className="logo-sub">Admin Panel</div>
          </div>
        </div>
        <div id="topbar-center">
          <div className="topbar-title">Platform Analitik Paneli</div>
          <div className="topbar-subtitle">
            İnşaat &amp; Vinç Sektörü — Türkiye geneli platform istatistikleri
            {stats?.lastUpdated && <> · {new Date(stats.lastUpdated).toLocaleString('tr-TR')}</>}
          </div>
        </div>
        <div id="topbar-right">
          <div className="badge-live">
            <div className="live-dot" />
            {stats ? 'Canlı Veri' : 'Veri Yok'}
          </div>
          <div className="btn-outline">
            <Calendar size={13} />
            {currentQuarterLabel()}
          </div>
          <button type="button" className="btn-outline" onClick={() => window.print()} disabled={!stats}>
            <FileDown size={13} />
            PDF İndir
          </button>
          <button type="button" className="btn-outline" onClick={() => exportFirmsCsv(filteredFirms)} disabled={!filteredFirms.length}>
            <Table size={13} />
            Excel İndir
          </button>
          <button type="button" className="btn-outline" onClick={() => { setNotifOpen(true); setNotifMsg(null); }}>
            <Bell size={13} />
            Bildirim Gönder
          </button>
          <button type="button" className="btn-primary" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw size={13} className={refreshing ? 'spin' : ''} />
            {refreshing ? 'Hesaplanıyor…' : 'Yenile'}
          </button>
          <Link to="/admin/legacy" className="btn-outline">Kullanıcı Yönetimi</Link>
          <div id="topbar-user">
            <div className="user-avatar"><Users size={14} /></div>
            <div>
              <div className="user-name">{userEmail || 'Admin'}</div>
              <div className="user-role">Super Admin</div>
            </div>
          </div>
          {onSignOut && (
            <button type="button" className="btn-outline" onClick={onSignOut} title="Çıkış">
              <LogOut size={13} />
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="empty-state" style={{ padding: '12px 28px', color: '#ef4444' }}>{error}</div>
      )}

      <div id="page-body">
        {!stats ? (
          <div className="empty-state">
            <p>İstatistik dökümanı bulunamadı.</p>
            <button type="button" className="btn-primary" onClick={handleRefresh} disabled={refreshing} style={{ margin: '12px auto 0' }}>
              İlk Hesaplamayı Başlat
            </button>
          </div>
        ) : (
          <>
            {/* KPI GRID */}
            <div className="kpi-grid">
              <div className="kpi-card">
                <div className="kpi-top">
                  <div className="kpi-icon-wrap"><Users size={16} /></div>
                  <TrendBadge value={trends.users} />
                </div>
                <div>
                  <div className="kpi-value">{formatNum(totals.registeredUserCount)}</div>
                  <div className="kpi-label">Kayıtlı Kullanıcı</div>
                </div>
                <div className="kpi-sub">{formatNum(totals.operatorCount)} operatör + {formatNum(totals.firmCount)} firma</div>
                <div className="kpi-sub">
                  Ayıklanmış: {formatNum(totals.firmWithOperatorsCount || 0)} aktif firma
                  {' · '}
                  {formatNum(totals.firmWithoutOperatorsCount ?? (totals.firmCount - (totals.firmWithOperatorsCount || 0)))} operatörsüz hariç
                </div>
              </div>
              <div className="kpi-card green">
                <div className="kpi-top">
                  <div className="kpi-icon-wrap green"><Truck size={16} /></div>
                  <TrendBadge value={trends.machines} />
                </div>
                <div>
                  <div className="kpi-value">{formatNum(totals.machineCount)}</div>
                  <div className="kpi-label">Aktif İş Makinesi</div>
                </div>
                <div className="kpi-sub">Vinç, ekskavatör, loder dahil</div>
              </div>
              <div className="kpi-card blue">
                <div className="kpi-top">
                  <div className="kpi-icon-wrap blue"><ClipboardCheck size={16} /></div>
                  <TrendBadge value={trends.puantaj} />
                </div>
                <div>
                  <div className="kpi-value">{formatCompact(totals.puantajCount)}</div>
                  <div className="kpi-label">Dijital Puantaj Kaydı</div>
                </div>
                <div className="kpi-sub">Toplam platform kaydı</div>
              </div>
              <div className="kpi-card purple">
                <div className="kpi-top">
                  <div className="kpi-icon-wrap purple"><Building2 size={16} /></div>
                  <TrendBadge value={trends.firms} />
                </div>
                <div>
                  <div className="kpi-value">{formatNum(totals.firmCount)}</div>
                  <div className="kpi-label">Kayıtlı Firma</div>
                </div>
                <div className="kpi-sub">
                  {formatNum(totals.firmWithOperatorsCount || 0)} operatörlü firma
                  {' · '}
                  {formatNum(totals.firmWithoutOperatorsCount ?? (totals.firmCount - (totals.firmWithOperatorsCount || 0)))} operatörsüz (hariç)
                </div>
              </div>
            </div>

            {/* CHARTS ROW */}
            <div className="charts-row">
              <div className="chart-card">
                <div className="chart-header">
                  <div>
                    <div className="chart-title">Aylık Platform Büyümesi</div>
                    <div className="chart-subtitle">Kullanıcı ve puantaj kaydı trendi (Son 8 ay)</div>
                  </div>
                  <div className="chart-legend">
                    <div className="legend-item"><div className="legend-dot" style={{ background: 'var(--primary)' }} />Kullanıcılar</div>
                    <div className="legend-item"><div className="legend-dot" style={{ background: '#3b82f6' }} />Puantaj</div>
                    <div className="legend-item"><div className="legend-dot" style={{ background: 'var(--success)' }} />Firmalar</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 130, paddingBottom: 14, minWidth: 36 }}>
                    <div className="y-label">{formatCompact(growthMax)}</div>
                    <div className="y-label">{formatCompact(growthMax * 0.75)}</div>
                    <div className="y-label">{formatCompact(growthMax * 0.5)}</div>
                    <div className="y-label">{formatCompact(growthMax * 0.25)}</div>
                    <div className="y-label">0</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height: 130 }}>
                      {monthlyGrowth.map((m) => (
                        <div key={m.month} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, flex: 1 }}>
                          <div style={{ display: 'flex', gap: 1, alignItems: 'flex-end', height: 116, width: '100%' }}>
                            <div className="barv" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                              <div className="bar-tip">{m.label} · Kullanıcı: {formatNum(m.users || m.firms || 0)}</div>
                              <div style={{ height: `${((m.users || m.firms || 0) / growthMax) * 116}px`, background: 'var(--primary)', opacity: 0.85, borderRadius: '2px 2px 0 0', minHeight: 2 }} />
                            </div>
                            <div className="barv" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                              <div className="bar-tip">{m.label} · Puantaj: {formatNum(m.puantaj || 0)}</div>
                              <div style={{ height: `${((m.puantaj || 0) / growthMax) * 116}px`, background: '#3b82f6', opacity: 0.85, borderRadius: '2px 2px 0 0', minHeight: 2 }} />
                            </div>
                            <div className="barv" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                              <div className="bar-tip">{m.label} · Aktif Firma: {formatNum(m.active || 0)}</div>
                              <div style={{ height: `${((m.active || m.firms || 0) / growthMax) * 116}px`, background: 'var(--success)', opacity: 0.85, borderRadius: '2px 2px 0 0', minHeight: 2 }} />
                            </div>
                          </div>
                          <div className="bar-label">{m.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="chart-card">
                <div className="chart-header">
                  <div>
                    <div className="chart-title">Filo Makine Dağılımı</div>
                    <div className="chart-subtitle">Kategori bazlı aktif makine</div>
                  </div>
                </div>
                <div className="donut-wrap">
                  <div className="donut-svg-wrap">
                    <svg width="120" height="120" viewBox="0 0 140 140">
                      <circle cx="70" cy="70" r="50" fill="none" stroke="#1e2330" strokeWidth="22" />
                      {donutSegments.map((seg, i) => (
                        <circle key={i} cx="70" cy="70" r="50" fill="none" stroke={seg.color} strokeWidth="22" strokeDasharray={seg.dasharray} strokeDashoffset={seg.offset}>
                          <title>{machineCategories[i]?.name}: {formatNum(machineCategories[i]?.count)} ({machineCategories[i]?.pct}%)</title>
                        </circle>
                      ))}
                      <text x="70" y="66" textAnchor="middle" fill="#e8eaf0" fontSize="18" fontWeight="700">
                        {formatCompact(machineCategoriesTotal)}
                      </text>
                      <text x="70" y="80" textAnchor="middle" fill="#6b7494" fontSize="9">Toplam</text>
                    </svg>
                  </div>
                  <div className="donut-legend-list">
                    {machineCategories.map((cat, i) => (
                      <div key={cat.name} className="donut-legend-item">
                        <div className="donut-legend-left">
                          <div className="legend-dot" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                          {cat.name}
                        </div>
                        <div className="donut-legend-bar-wrap">
                          <div style={{ width: 70, height: 3, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
                            <div style={{ width: `${cat.pct}%`, height: '100%', background: DONUT_COLORS[i % DONUT_COLORS.length], borderRadius: 99 }} />
                          </div>
                          <div className="donut-pct">{cat.pct}%</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* MID ROW */}
            <div className="mid-row">
              {/* Rol dağılımı */}
              <div className="audience-card">
                <div>
                  <div className="chart-title">Kullanıcı Rol Dağılımı</div>
                  <div className="chart-subtitle" style={{ marginTop: 2 }}>
                    Operatör ve makine sahibi dağılımı
                  </div>
                </div>
                <div className="stat-list">
                  {[
                    { label: 'Operatör & Sürücü', pct: roleDistribution.operatorPct || 0, count: roleDistribution.operatorCount || 0, color: 'var(--primary)' },
                    { label: 'Makine Sahibi', pct: roleDistribution.ownerPct || 0, count: roleDistribution.ownerCount || 0, color: '#3b82f6' },
                  ].map((row) => (
                    <div key={row.label} className="stat-item">
                      <div className="stat-label">{row.label} <span style={{ color: 'var(--foreground)', fontWeight: 600 }}>({formatNum(row.count)})</span></div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <div style={{ width: 90, height: 3, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
                          <div style={{ width: `${row.pct}%`, height: '100%', background: row.color, borderRadius: 99 }} />
                        </div>
                        <div className="stat-value">{row.pct}%</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                  <div className="stat-list">
                    <div className="stat-item">
                      <div className="stat-label">Ort. Günlük Oturum</div>
                      <div className="stat-value">{activityApprox.sessionAvg || '—'}</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-label">Aylık Aktif Kullanıcı</div>
                      <div className="stat-value">{formatNum(activityApprox.monthlyActive)}</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-label">Bildirim Açılma Oranı</div>
                      <div className="stat-value">{activityApprox.notificationRate || '—'}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Günlük aktif kullanım */}
              <div className="audience-card">
                <div>
                  <div className="chart-title">Günlük Aktif Kullanım</div>
                  <div className="chart-subtitle" style={{ marginTop: 2 }}>
                    Son 14 günlük DAU trendi {dauApprox.approx && <span className="approx-tag">(~yaklaşık)</span>}
                  </div>
                </div>
                <div className="mini-sparkline">
                  {(dauApprox.series || []).map((d, i) => {
                    const h = Math.max(6, (d.count / dauMax) * 100);
                    const hi = d.count === dauMax && dauMax > 0;
                    return (
                      <div key={i} className={`spark-bar ${hi ? 'hi' : ''}`} style={{ height: `${h}%` }}>
                        <div className="spark-tooltip">{formatCompact(d.count)}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="stat-list">
                  <div className="stat-item">
                    <div className="stat-label">Ortalama DAU</div>
                    <div className="stat-value">{formatNum(dauApprox.avg)}</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-label">Peak Kullanım Saati</div>
                    <div className="stat-value">{activityApprox.peakHour || '—'}</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-label">Mobil / Web Oranı</div>
                    <div className="stat-value">{activityApprox.mobileWebRatio || '—'}</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-label">Android / iOS</div>
                    <div className="stat-value">{activityApprox.androidIosRatio || '—'}</div>
                  </div>
                </div>
              </div>

              {/* Puantaj kayıt dağılımı */}
              <div className="audience-card">
                <div>
                  <div className="chart-title">Puantaj Kayıt Dağılımı</div>
                  <div className="chart-subtitle" style={{ marginTop: 2 }}>
                    Modül bazlı kayıt dağılımı {stats.puantajByModule?.some((x) => x) && <span className="approx-tag">(~yaklaşık)</span>}
                  </div>
                </div>
                <div className="funnel-list">
                  {puantajByModule.map((mod, i) => (
                    <div key={mod.name} className="funnel-item">
                      <div className="funnel-row">
                        <div className="funnel-name">{mod.name}</div>
                        <div className="funnel-val">{formatNum(mod.monthlyCount)}</div>
                      </div>
                      <div className="funnel-bar-bg">
                        <div className="funnel-bar-fill" style={{ width: `${mod.pct}%`, background: FUNNEL_COLORS[i % FUNNEL_COLORS.length] }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                  <div className="stat-item">
                    <div className="stat-label">Toplam Puantaj</div>
                    <div className="stat-value" style={{ color: 'var(--primary)' }}>{formatNum(totals.puantajCount)} kayıt</div>
                  </div>
                </div>
              </div>
            </div>

            {/* TABLE + REGIONS */}
            <div className="bottom-row">
              <div className="table-card">
                <div className="chart-header">
                  <div>
                    <div className="chart-title">En Aktif Firmalar</div>
                    <div className="chart-subtitle" style={{ marginTop: 2 }}>Makine sayısı ve puantaj aktivitesine göre</div>
                  </div>
                </div>
                <table className="data-table">
                  <thead>
                    <tr><th>Firma</th><th>Makine</th><th>Puantaj</th><th>Durum</th></tr>
                  </thead>
                  <tbody>
                    {topFirms.slice(0, 10).map((f, i) => {
                      const st = statusLabel(f.status);
                      const c = DONUT_COLORS[i % DONUT_COLORS.length];
                      return (
                        <tr key={f.id} className="firma-row" onClick={() => openFirmDetail(f)} style={{ cursor: 'pointer' }}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                              <div className="td-icon-wrap" style={{ background: `${c}1a` }}>
                                <Building2 size={12} color={c} />
                              </div>
                              <span style={{ fontWeight: 500 }}>{f.name}</span>
                            </div>
                          </td>
                          <td>{f.machineCount}</td>
                          <td>{formatNum(f.puantajCount)}</td>
                          <td><span className={`status-badge ${st.cls}`}>{st.text}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="map-card">
                <div className="chart-header">
                  <div>
                    <div className="chart-title">Bölgesel Kullanım Dağılımı</div>
                    <div className="chart-subtitle" style={{ marginTop: 2 }}>
                      İl bazlı yoğunluk <span className="approx-tag">(~yaklaşık)</span>
                    </div>
                  </div>
                  <div className="badge-live" style={{ fontSize: 10, padding: '3px 7px' }}>
                    <div className="live-dot" />TR
                  </div>
                </div>
                <div className="region-list">
                  {regions.length ? regions.map((r, i) => {
                    const colorClass = ['', '', 'blue', 'blue', 'green', 'green', 'purple', 'purple'][i] || 'purple';
                    return (
                      <div key={r.name} className="region-item">
                        <div className="region-header">
                          <div className="region-name">{r.name}</div>
                          <div className="region-count">{formatNum(r.count)}</div>
                        </div>
                        <div className="region-bar-bg">
                          <div className={`region-bar-fill ${colorClass}`.trim()} style={{ width: `${r.pct}%` }} />
                        </div>
                      </div>
                    );
                  }) : <div className="stat-label">Adres verisi yeterli değil</div>}
                </div>
              </div>
            </div>

            {/* PUANTAJ KAYIT ANALİZİ */}
            <div className="section-divider">
              <div className="section-divider-title">Puantaj Kayıt Analizi</div>
              <div className="section-divider-line" />
            </div>

            <div className="chart-card">
              <div className="chart-header">
                <div>
                  <div className="chart-title">Girilen Puantaj Sayısı</div>
                  <div className="chart-subtitle">Zaman dilimine göre platform genelinde toplam puantaj kaydı</div>
                </div>
                <div className="ptab-group">
                  {[
                    { id: 'daily', label: 'Günlük' },
                    { id: 'weekly', label: 'Haftalık' },
                    { id: 'monthly', label: 'Aylık' },
                  ].map((t) => (
                    <div key={t.id} className={`ptab ${puantajTab === t.id ? 'active' : ''}`} onClick={() => setPuantajTab(t.id)}>
                      {t.label}
                    </div>
                  ))}
                </div>
              </div>

              {puantajTab !== 'cumulative' && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 150, paddingBottom: 16, minWidth: 34 }}>
                    <div className="y-label">{formatCompact(puantajMax)}</div>
                    <div className="y-label">{formatCompact(puantajMax * 0.75)}</div>
                    <div className="y-label">{formatCompact(puantajMax * 0.5)}</div>
                    <div className="y-label">{formatCompact(puantajMax * 0.25)}</div>
                    <div className="y-label">0</div>
                  </div>
                  <div style={{ flex: 1, overflow: 'visible' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 150 }}>
                      {puantajSeries.map((p, idx) => (
                        <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, flex: 1, minWidth: 18 }}>
                          <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 132, width: '100%' }}>
                            <div className="barv" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                              <div className="bar-tip">{p.label}: {formatNum(p.count || 0)} puantaj</div>
                              <div style={{ height: `${((p.count || 0) / puantajMax) * 132}px`, background: 'var(--primary)', borderRadius: '2px 2px 0 0', minHeight: 2 }} />
                            </div>
                            {hasApproved && (
                              <div className="barv" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                                <div className="bar-tip">{p.label} · Onaylanan: {formatNum(p.approved || 0)}</div>
                                <div style={{ height: `${((p.approved || 0) / puantajMax) * 132}px`, background: '#3b82f6', opacity: 0.8, borderRadius: '2px 2px 0 0', minHeight: 2 }} />
                              </div>
                            )}
                          </div>
                          <div className="bar-label">{p.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <div className="chart-legend">
                  <div className="legend-item"><div className="legend-dot" style={{ background: 'var(--primary)' }} />Yeni Puantaj</div>
                  {hasApproved && <div className="legend-item"><div className="legend-dot" style={{ background: '#3b82f6' }} />Onaylanan Puantaj</div>}
                </div>
                <div style={{ display: 'flex', gap: 18 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--foreground)' }}>{formatNum(thisWeek)}</div>
                    <div style={{ fontSize: 10, color: 'var(--muted-foreground)' }}>Bu Hafta</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 17, fontWeight: 700, color: '#3b82f6' }}>{formatNum(lastWeek)}</div>
                    <div style={{ fontSize: 10, color: 'var(--muted-foreground)' }}>Geçen Hafta</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 17, fontWeight: 700, color: weekChange >= 0 ? 'var(--success)' : 'var(--destructive)' }}>
                      {weekChange >= 0 ? '+' : ''}{weekChange}%
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--muted-foreground)' }}>Değişim</div>
                  </div>
                </div>
              </div>

              {/* Kümülatif Büyüme — Haftalık (2025 Haziran'dan bugüne) */}
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--foreground)' }}>Kümülatif Büyüme (Haftalık)</div>
                    <div style={{ fontSize: 10, color: 'var(--muted-foreground)' }}>
                      {cumMode === 'puantaj'
                        ? '2025 Haziran’dan bu yana haftalık kümülatif puantaj'
                        : '2025 Haziran’dan bu yana haftalık kümülatif firma — toplam vs ayıklanmış (operatörlü)'}
                    </div>
                  </div>
                  <div className="ptab-group">
                    {[
                      { id: 'puantaj', label: 'Puantaj' },
                      { id: 'kullanici', label: 'Kullanıcı' },
                    ].map((t) => (
                      <div key={t.id} className={`ptab ${cumMode === t.id ? 'active' : ''}`} onClick={() => setCumMode(t.id)}>
                        {t.label}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="chart-legend" style={{ marginBottom: 6 }}>
                  {cumMode === 'kullanici' ? (
                    <>
                      <div className="legend-item"><div className="legend-dot" style={{ background: '#f59e0b' }} />Toplam firma</div>
                      <div className="legend-item"><div className="legend-dot" style={{ background: '#22c55e' }} />Ayıklanmış (operatörlü)</div>
                    </>
                  ) : (
                    <>
                      <div className="legend-item"><div className="legend-dot" style={{ background: '#f59e0b' }} />Tüm puantaj</div>
                      <div className="legend-item"><div className="legend-dot" style={{ background: '#3b82f6' }} />Seferoğlu Hafriyat hariç</div>
                    </>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 150, paddingBottom: 4, minWidth: 34 }}>
                    <div className="y-label">{formatCompact(cumChart.max)}</div>
                    <div className="y-label">{formatCompact(cumChart.max * 0.75)}</div>
                    <div className="y-label">{formatCompact(cumChart.max * 0.5)}</div>
                    <div className="y-label">{formatCompact(cumChart.max * 0.25)}</div>
                    <div className="y-label">0</div>
                  </div>
                  <div
                    style={{ flex: 1, position: 'relative', cursor: 'crosshair' }}
                    onMouseMove={(e) => {
                      const n = growthWeekly.length;
                      if (!n) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      const ratio = (e.clientX - rect.left) / rect.width;
                      let idx = Math.round(ratio * (n - 1));
                      idx = Math.max(0, Math.min(n - 1, idx));
                      setCumHover(idx);
                    }}
                    onMouseLeave={() => setCumHover(null)}
                  >
                    <svg viewBox="0 0 900 150" preserveAspectRatio="none" style={{ width: '100%', height: 150, display: 'block' }}>
                      <defs>
                        <linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.26" />
                          <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.02" />
                        </linearGradient>
                      </defs>
                      {[0.25, 0.5, 0.75, 1].map((g, gi) => (
                        <line key={gi} x1="0" y1={150 * g} x2="900" y2={150 * g} stroke="var(--border)" strokeWidth="1" opacity="0.4" />
                      ))}
                      {cumChart.primary.area && <path d={cumChart.primary.area} fill="url(#growthGrad)" />}
                      {cumChart.primary.line && (
                        <path d={cumChart.primary.line} fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                      )}
                      {cumChart.secondary && cumChart.secondary.line && (
                        <path d={cumChart.secondary.line} fill="none" stroke={cumSecondaryColor} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                      )}
                      {cumChart.primary.points.length > 0 && (() => {
                        const p = cumChart.primary.points[cumChart.primary.points.length - 1];
                        return <circle cx={p.x} cy={p.y} r="3.5" fill="#f59e0b" />;
                      })()}
                      {cumChart.secondary && cumChart.secondary.points.length > 0 && (() => {
                        const p = cumChart.secondary.points[cumChart.secondary.points.length - 1];
                        return <circle cx={p.x} cy={p.y} r="3.5" fill={cumSecondaryColor} />;
                      })()}
                      {cumHover != null && cumChart.primary.points[cumHover] && (
                        <>
                          <line
                            x1={cumChart.primary.points[cumHover].x}
                            y1="0"
                            x2={cumChart.primary.points[cumHover].x}
                            y2="150"
                            stroke="var(--muted-foreground)"
                            strokeWidth="1"
                            strokeDasharray="4 3"
                            opacity="0.6"
                            vectorEffect="non-scaling-stroke"
                          />
                          <circle cx={cumChart.primary.points[cumHover].x} cy={cumChart.primary.points[cumHover].y} r="4" fill="#f59e0b" />
                          {cumChart.secondary && cumChart.secondary.points[cumHover] && (
                            <circle cx={cumChart.secondary.points[cumHover].x} cy={cumChart.secondary.points[cumHover].y} r="4" fill={cumSecondaryColor} />
                          )}
                        </>
                      )}
                    </svg>
                    <div style={{ position: 'absolute', top: 2, right: 4, textAlign: 'right', pointerEvents: 'none' }}>
                      {cumMode === 'puantaj' ? (
                        <>
                          <div style={{ fontSize: 18, fontWeight: 700, color: '#f59e0b' }}>{formatCompact(growthLast?.puantaj || 0)}</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#3b82f6' }}>{formatCompact(growthLast?.puantajExcl || 0)}</div>
                        </>
                      ) : (
                        <>
                          <div style={{ fontSize: 18, fontWeight: 700, color: '#f59e0b' }}>{formatNum(growthLast?.users || 0)}</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#22c55e' }}>{formatNum(growthLast?.usersFiltered || 0)}</div>
                        </>
                      )}
                    </div>
                    {cumHover != null && growthWeekly[cumHover] && (() => {
                      const d = growthWeekly[cumHover];
                      const n = growthWeekly.length;
                      const ratio = n > 1 ? cumHover / (n - 1) : 0;
                      const dateStr = d.weekStart
                        ? new Date(d.weekStart).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })
                        : d.label;
                      const tx = ratio > 0.7 ? '-100%' : ratio < 0.15 ? '0%' : '-50%';
                      return (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: `${ratio * 100}%`,
                            transform: `translateX(${tx})`,
                            background: 'var(--card, #1a1f2e)',
                            border: '1px solid var(--border)',
                            borderRadius: 8,
                            padding: '6px 9px',
                            pointerEvents: 'none',
                            boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
                            whiteSpace: 'nowrap',
                            zIndex: 5,
                          }}
                        >
                          <div style={{ fontSize: 10, color: 'var(--muted-foreground)', marginBottom: 2 }}>{dateStr} haftası</div>
                          {cumMode === 'puantaj' ? (
                            <>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#f59e0b' }}>{formatNum(d.puantaj || 0)} tüm puantaj</div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#3b82f6' }}>{formatNum(d.puantajExcl || 0)} hariç</div>
                            </>
                          ) : (
                            <>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#f59e0b' }}>{formatNum(d.users || 0)} toplam firma</div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#22c55e' }}>{formatNum(d.usersFiltered || 0)} ayıklanmış</div>
                            </>
                          )}
                        </div>
                      );
                    })()}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                      {growthTicks.map((t, i) => (
                        <span key={i} style={{ fontSize: 9, color: 'var(--muted-foreground)' }}>{t.label}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* FİRMA LİSTESİ */}
            <div className="section-divider">
              <div className="section-divider-title">Firma Listesi</div>
              <div className="section-divider-line" />
            </div>

            <div id="firma-list-section">
              <div className="chart-header" style={{ flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <div className="chart-title">Kayıtlı Firmalar — Detaylı Liste</div>
                  <div className="chart-subtitle">Tüm üye firmalar, iletişim ve makine bilgileri</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div className="firma-search-bar">
                    <Search size={13} color="#6b7494" />
                    <input type="search" placeholder="Firma ara..." value={firmaSearch} onChange={(e) => setFirmaSearch(e.target.value)} />
                  </div>
                  {[
                    { id: 'active', label: 'Aktif', icon: <CheckCircle size={11} /> },
                    { id: 'idle', label: 'Pasif', icon: <CircleSlash size={11} /> },
                  ].map((chip) => (
                    <div key={chip.id} className={`filter-chip ${firmaFilter === chip.id ? 'active' : ''}`} onClick={() => setFirmaFilter(firmaFilter === chip.id ? 'all' : chip.id)}>
                      {chip.icon}{chip.label}
                    </div>
                  ))}
                  <div style={{ width: 1, height: 20, background: 'var(--border)', margin: '0 2px' }} />
                  <div className="sort-select">
                    <ArrowUpDown size={11} color="#6b7494" />
                    Sırala: {sortLabel}
                    <ChevronDown size={11} color="#6b7494" style={{ marginLeft: 2 }} />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 10, color: 'var(--muted-foreground)', whiteSpace: 'nowrap' }}>Sıralama:</span>
                {[
                  { key: 'createdAt', dir: 'desc', label: 'Yeniden Eskiye' },
                  { key: 'createdAt', dir: 'asc', label: 'Eskiden Yeniye' },
                  { key: 'puantajCount', dir: 'desc', label: 'Puantaj Sayısı ↓' },
                  { key: 'machineCount', dir: 'desc', label: 'Makine Sayısı ↓' },
                  { key: 'name', dir: 'asc', label: 'Alfabetik (A–Z)' },
                  { key: 'name', dir: 'desc', label: 'Alfabetik (Z–A)' },
                ].map((opt) => {
                  const active = firmaSort.key === opt.key && firmaSort.dir === opt.dir;
                  return (
                    <div key={opt.label} className={`filter-chip ${active ? 'active' : ''}`} style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => setSort(opt.key, opt.dir)}>
                      {opt.label}
                    </div>
                  );
                })}
              </div>

              <table className="firma-table">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => toggleSort('name')}>Firma Adı <span className="sort-icon"><ChevronsUpDown size={10} /></span></th>
                    <th className="sortable" onClick={() => toggleSort('contact')}>Yetkili Kişi</th>
                    <th>İletişim</th>
                    <th>Adres</th>
                    <th className="sortable" onClick={() => toggleSort('machineCount')}>Makine Sayısı <span className="sort-icon"><ChevronsUpDown size={10} /></span></th>
                    <th className="sortable" onClick={() => toggleSort('puantajCount')}>Puantaj / Ay <span className="sort-icon"><ChevronsUpDown size={10} /></span></th>
                    <th className="sortable" onClick={() => toggleSort('createdAt')}>Kayıt Tarihi <span className="sort-icon"><ChevronsUpDown size={10} /></span></th>
                    <th>Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedFirms.map((f, i) => {
                    const st = statusLabel(f.status);
                    const c = DONUT_COLORS[i % DONUT_COLORS.length];
                    return (
                      <tr key={f.id} className="firma-row" onClick={() => openFirmDetail(f)} style={{ cursor: 'pointer' }}>
                        <td>
                          <div className="firma-name-cell">
                            <div className="firma-icon-wrap" style={{ background: `${c}1a` }}>
                              <Building2 size={13} color={c} />
                            </div>
                            <div>
                              <div className="firma-name">{f.name}</div>
                              <div className="firma-email">{f.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="firma-contact-name">{f.contact}</div>
                          <div className="firma-contact-phone">{f.phone}</div>
                        </td>
                        <td style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{f.email}</td>
                        <td><div className="firma-address">{f.address}</div></td>
                        <td>
                          <div className="machine-count-badge" style={{ background: `${c}1f`, color: c }}>
                            <Truck size={10} />{f.machineCount}
                          </div>
                        </td>
                        <td style={{ fontSize: 12, fontWeight: 600, color: 'var(--foreground)' }}>{formatNum(f.puantajCount)}</td>
                        <td style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{formatTrDate(f.createdAt)}</td>
                        <td><span className={`status-badge ${st.cls}`}>{st.text}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 4, flexWrap: 'wrap', gap: 10 }}>
                <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
                  Toplam <span style={{ color: 'var(--foreground)', fontWeight: 600 }}>{formatNum(filteredFirms.length)}</span> firmadan{' '}
                  <span style={{ color: 'var(--foreground)', fontWeight: 600 }}>{pagedFirms.length}</span> tanesi gösteriliyor
                </div>
                <div className="pagination">
                  <button type="button" className="page-btn wide" disabled={firmaPage <= 1} onClick={() => setFirmaPage((p) => Math.max(1, p - 1))}>← Önceki</button>
                  {Array.from({ length: Math.min(firmaPageCount, 5) }, (_, i) => {
                    let pageNum = i + 1;
                    if (firmaPageCount > 5) {
                      if (firmaPage <= 3) pageNum = i + 1;
                      else if (firmaPage >= firmaPageCount - 2) pageNum = firmaPageCount - 4 + i;
                      else pageNum = firmaPage - 2 + i;
                    }
                    return (
                      <div key={pageNum} className={`page-btn ${firmaPage === pageNum ? 'active' : ''}`} onClick={() => setFirmaPage(pageNum)}>{pageNum}</div>
                    );
                  })}
                  <button type="button" className="page-btn wide" disabled={firmaPage >= firmaPageCount} onClick={() => setFirmaPage((p) => Math.min(firmaPageCount, p + 1))}>Sonraki →</button>
                </div>
              </div>
            </div>

            {/* MAKİNE MODEL DAĞILIMI */}
            {machineModels.length > 0 && (
              <>
                <div className="section-divider">
                  <div className="section-divider-title">Makine Model Dağılımı</div>
                  <div className="section-divider-line" />
                </div>
                <div id="machine-model-full">
                  <div className="chart-header">
                    <div>
                      <div className="chart-title">Platform Genelinde Kullanılan Makine Modelleri</div>
                      <div className="chart-subtitle">Tüm firmalar genelinde kayıtlı makine marka ve modelleri</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>Toplam</span>
                      <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--foreground)' }}>{formatNum(machineCategoriesTotal)}</span>
                      <span style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>aktif makine</span>
                    </div>
                  </div>

                  <div className="model-grid-inner">
                    {machineModels.map((model, idx) => {
                      const theme = CATEGORY_THEME[idx % CATEGORY_THEME.length];
                      const pct = machineCategoriesTotal ? Math.round((model.count / machineCategoriesTotal) * 100) : 0;
                      const brandMax = maxOf(model.brands || [], 'count');
                      return (
                        <div key={model.type} className="model-kpi-card">
                          <div className="model-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                              <div className="model-icon" style={{ background: theme.bg }}>
                                <Truck size={14} color={theme.color} />
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--foreground)' }}>{model.type}</span>
                            </div>
                            <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 'var(--radius-xl)', background: theme.chipBg, color: theme.color }}>{pct}%</span>
                          </div>
                          <div className="model-count">{formatNum(model.count)}</div>
                          <div className="model-label">Toplam kayıtlı {model.type.toLowerCase()}</div>
                          <div className="model-brands">
                            {(model.brands || []).map((b, bi) => (
                              <div key={b.name}>
                                <div className="model-brand-row">
                                  <div className="model-brand-name">{b.name}</div>
                                  <div className="model-brand-val">{formatNum(b.count)}</div>
                                </div>
                                <div className="model-bar-bg">
                                  <div className="model-bar-fill" style={{ width: `${(b.count / brandMax) * 100}%`, background: theme.color, opacity: 1 - bi * 0.18 }} />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {topModels.length > 0 && (
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--foreground)', marginBottom: 10 }}>
                        En Çok Kullanılan Makine Modelleri (Top {topModels.length})
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
                        {topModels.map((m, i) => (
                          <div key={m.name} className="model-top-item">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                              <div style={{ width: 5, height: 5, borderRadius: '50%', background: DONUT_COLORS[i % DONUT_COLORS.length], flexShrink: 0 }} />
                              <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--foreground)' }}>{m.name}</span>
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--foreground)' }}>{formatNum(m.count)} adet</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            <div style={{ height: 24 }} />
          </>
        )}
      </div>

      {notifOpen && (
        <div className="notif-overlay" onClick={() => !notifLoading && setNotifOpen(false)}>
          <div className="notif-modal" onClick={(e) => e.stopPropagation()}>
            {notifLoading && (
              <div className="notif-loading">
                <RefreshCw size={30} className="spin" color="#f59e0b" />
                <div className="notif-loading-text">Bildirim gönderiliyor…</div>
                <div className="notif-loading-sub">Lütfen bekleyin, cihazlara iletiliyor.</div>
              </div>
            )}
            <div className="notif-head">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Bell size={16} color="var(--primary)" />
                <span style={{ fontSize: 15, fontWeight: 700 }}>Bildirim Gönder</span>
              </div>
              <button type="button" className="notif-close" onClick={() => !notifLoading && setNotifOpen(false)}>
                <X size={16} />
              </button>
            </div>

            <div className="notif-body">
              <label className="notif-label">Başlık</label>
              <input
                className="notif-input"
                type="text"
                value={notifTitle}
                maxLength={80}
                placeholder="Bildirim başlığı"
                onChange={(e) => setNotifTitle(e.target.value)}
              />

              <label className="notif-label">Mesaj</label>
              <textarea
                className="notif-input"
                rows={3}
                value={notifBody}
                maxLength={300}
                placeholder="Bildirim mesajı"
                onChange={(e) => setNotifBody(e.target.value)}
              />

              <label className="notif-label">Hedef</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className={`filter-chip ${notifMode === 'all' ? 'active' : ''}`}
                  onClick={() => { setNotifMode('all'); setNotifPreview(null); }}
                >
                  Tüm Kullanıcılar
                </button>
                <button
                  type="button"
                  className={`filter-chip ${notifMode === 'selected' ? 'active' : ''}`}
                  onClick={() => { setNotifMode('selected'); setNotifPreview(null); }}
                >
                  Seçili Firmalar ({notifSelected.length})
                </button>
              </div>

              {notifMode === 'selected' && (
                <div className="notif-firma-box">
                  <div className="firma-search-bar" style={{ marginBottom: 8 }}>
                    <Search size={13} color="#6b7494" />
                    <input
                      type="search"
                      placeholder="Firma ara..."
                      value={notifSearch}
                      onChange={(e) => setNotifSearch(e.target.value)}
                    />
                  </div>
                  <div className="notif-firma-list">
                    {notifFirms.slice(0, 200).map((f) => (
                      <label key={f.id} className="notif-firma-item">
                        <input
                          type="checkbox"
                          checked={notifSelected.includes(f.email)}
                          onChange={() => toggleNotifEmail(f.email)}
                        />
                        <span style={{ flex: 1 }}>{f.name}</span>
                        <span style={{ color: 'var(--muted-foreground)', fontSize: 10 }}>{f.email}</span>
                      </label>
                    ))}
                    {notifFirms.length === 0 && <div className="stat-label" style={{ padding: 8 }}>Firma bulunamadı</div>}
                  </div>
                </div>
              )}

              {notifPreview && (
                <div className="notif-preview">
                  Hedef: {notifPreview.userCount} kullanıcı · {notifPreview.totalTokenCount} cihaz
                  {notifPreview.usersWithoutTokens?.length > 0 && ` · ${notifPreview.usersWithoutTokens.length} kullanıcıda cihaz yok`}
                </div>
              )}

              {notifMsg && (
                <div className={`notif-msg ${notifMsg.type}`}>{notifMsg.text}</div>
              )}
            </div>

            <div className="notif-foot">
              <button type="button" className="btn-outline" onClick={handlePreviewNotif} disabled={notifPreviewLoading || notifLoading}>
                {notifPreviewLoading ? 'Hesaplanıyor…' : 'Önizle (kaç cihaz?)'}
              </button>
              <button type="button" className="btn-primary" onClick={handleSendNotif} disabled={notifLoading}>
                <Bell size={13} />
                {notifLoading ? 'Gönderiliyor…' : 'Gönder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {notifSuccess && (
        <div className="notif-overlay" style={{ zIndex: 1100 }} onClick={closeNotifSuccess}>
          <div className="notif-success" onClick={(e) => e.stopPropagation()}>
            <div className="notif-success-icon"><CheckCircle size={34} color="#10b981" /></div>
            <div className="notif-success-title">Bildirim Gönderildi</div>
            <div className="notif-success-text">
              Bildirim başarıyla <b>{formatNum(notifSuccess.successCount)}</b> cihaza
              {' '}(<b>{formatNum(notifSuccess.totalUsers)}</b> kullanıcı) gönderildi.
              {notifSuccess.failureCount > 0 && (
                <div style={{ marginTop: 6, color: 'var(--muted-foreground)', fontSize: 12 }}>
                  {formatNum(notifSuccess.failureCount)} cihaza ulaşılamadı.
                </div>
              )}
            </div>
            <button type="button" className="btn-primary notif-success-btn" onClick={closeNotifSuccess}>
              Tamam
            </button>
          </div>
        </div>
      )}

      {detailFirm && (
        <div className="notif-overlay" onClick={() => setDetailFirm(null)}>
          <div className="fd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fd-head">
              <div className="fd-head-left">
                <div className="fd-logo"><Building2 size={22} color="var(--primary)" /></div>
                <div>
                  <div className="fd-name">{detailFirm.name}</div>
                  <div className="fd-sub">
                    <MapPin size={11} /> {detailFirm.address || '-'}
                    <span style={{ color: 'var(--border)', margin: '0 3px' }}>•</span>
                    <Calendar size={11} /> Kayıt: {formatTrDate(detailFirm.createdAt)}
                  </div>
                </div>
              </div>
              <div className="fd-head-right">
                <span className={`status-badge ${statusLabel(detailFirm.status).cls}`}>
                  ● {statusLabel(detailFirm.status).text}
                </span>
                <button type="button" className="btn-outline" onClick={() => sendNotifToFirm(detailFirm)}>
                  <Bell size={12} /> Bildirim Gönder
                </button>
                <button type="button" className="fd-close" onClick={() => setDetailFirm(null)}>
                  <X size={15} />
                </button>
              </div>
            </div>

            <div className="fd-body">
              <div className="fd-kpi-row">
                <div className="detail-kpi">
                  <div className="detail-kpi-val">{formatNum(detailFirm.machineCount)}</div>
                  <div className="detail-kpi-label">Kayıtlı Makine</div>
                </div>
                <div className="detail-kpi blue">
                  <div className="detail-kpi-val">{formatNum(detailFirm.puantajCount)}</div>
                  <div className="detail-kpi-label">Toplam Puantaj</div>
                </div>
                <div className="detail-kpi green">
                  <div className="detail-kpi-val">{formatNum(detailFirm.operatorCount)}</div>
                  <div className="detail-kpi-label">Operatör</div>
                </div>
                <div className="detail-kpi purple">
                  <div className="detail-kpi-val">{formatNum(detailFirm.kontorAmount)}</div>
                  <div className="detail-kpi-label">Kontör</div>
                </div>
                <div className="detail-kpi pink">
                  <div className="detail-kpi-val" style={{ fontSize: 13 }}>{detailFirm.lastPuantaj ? formatTrDate(detailFirm.lastPuantaj) : '—'}</div>
                  <div className="detail-kpi-label">Son Puantaj</div>
                </div>
              </div>

              <div className="fd-info-grid">
                <div className="info-card">
                  <div className="info-card-title"><Building2 size={12} color="var(--primary)" /> Firma Bilgileri</div>
                  <div className="fd-info-rows">
                    <div className="info-row"><div className="info-label">Firma Adı</div><div className="info-value">{detailFirm.name}</div></div>
                    <div className="info-row"><div className="info-label">Durum</div><div className="info-value">{statusLabel(detailFirm.status).text}</div></div>
                    <div className="info-row"><div className="info-label">Kayıt Tarihi</div><div className="info-value">{formatTrDate(detailFirm.createdAt)}</div></div>
                    <div className="info-row"><div className="info-label"><MapPin size={9} style={{ verticalAlign: 'middle' }} /> Şehir</div><div className="info-value">{detailFirm.city || 'Belirlenemedi'}</div></div>
                    <div className="info-row"><div className="info-label">Platform</div><div className="info-value">{detailFirm.platform || '-'}</div></div>
                    <div className="info-row" style={{ gridColumn: '1/-1' }}><div className="info-label">Adres</div><div className="info-value">{detailFirm.address || '-'}</div></div>
                  </div>
                </div>

                <div className="info-card">
                  <div className="info-card-title"><UserCircle size={12} color="#3b82f6" /> Yetkili Kişi &amp; İletişim</div>
                  <div className="fd-info-rows">
                    <div className="info-row"><div className="info-label">Yetkili</div><div className="info-value">{detailFirm.contact || '-'}</div></div>
                    <div className="info-row"><div className="info-label">Son Puantaj</div><div className="info-value">{detailFirm.lastPuantaj ? formatTrDate(detailFirm.lastPuantaj) : '—'}</div></div>
                    <div className="info-row" style={{ gridColumn: '1/-1' }}>
                      <div className="info-label"><Mail size={9} style={{ verticalAlign: 'middle' }} /> E-Posta</div>
                      <div className="info-value">{detailFirm.email || '-'}</div>
                    </div>
                    <div className="info-row" style={{ gridColumn: '1/-1' }}>
                      <div className="info-label"><Phone size={9} style={{ verticalAlign: 'middle' }} /> Telefon</div>
                      <div className="info-value">{detailFirm.phone || '-'}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="info-card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div className="info-card-title"><Truck size={12} color="var(--success)" /> Makine Envanteri</div>
                  <div style={{ fontSize: 10, color: 'var(--muted-foreground)' }}>{formatNum(detailFirm.machineCount)} toplam</div>
                </div>
                {detailLoading ? (
                  <div className="stat-label" style={{ padding: '12px 0' }}>Makineler yükleniyor…</div>
                ) : detailMachines && detailMachines.length > 0 ? (
                  <table className="machine-table">
                    <thead>
                      <tr><th>Marka / Model</th><th>Tür</th><th>Adet</th></tr>
                    </thead>
                    <tbody>
                      {detailMachines.slice(0, 30).map((m, i) => (
                        <tr key={i}>
                          <td>
                            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--foreground)' }}>
                              {(m.brand || m.model) ? `${m.brand} ${m.model}`.trim() : 'Bilinmiyor'}
                            </div>
                          </td>
                          <td>
                            <span className="machine-type-badge" style={{ background: 'rgba(245,158,11,0.12)', color: 'var(--primary)' }}>
                              {m.type}
                            </span>
                          </td>
                          <td style={{ fontWeight: 600 }}>{m.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="stat-label" style={{ padding: '12px 0' }}>Bu firmada kayıtlı makine bulunamadı.</div>
                )}
              </div>

              <div className="fd-info-grid">
                <div className="info-card">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div className="info-card-title"><Users size={12} color="#3b82f6" /> Operatörler</div>
                    <div style={{ fontSize: 10, color: 'var(--muted-foreground)' }}>{formatNum(detailFirm.operatorCount)} kişi</div>
                  </div>
                  {detailLoading && !detailOperators ? (
                    <div className="stat-label" style={{ padding: '10px 0' }}>Yükleniyor…</div>
                  ) : detailOperators && detailOperators.length > 0 ? (
                    <div className="fd-op-list">
                      {detailOperators.map((o) => (
                        <div key={o.id} className="fd-op-item">
                          <span style={{ fontWeight: 500 }}>{o.name}</span>
                          <span style={{ color: 'var(--muted-foreground)', fontSize: 11 }}>{o.phone}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="stat-label" style={{ padding: '10px 0' }}>Operatör bulunamadı.</div>
                  )}
                </div>

                <div className="info-card">
                  <div className="info-card-title"><ClipboardCheck size={12} color="var(--primary)" /> Son 10 Puantaj</div>
                  {detailLoading && !detailPuantajlar ? (
                    <div className="stat-label" style={{ padding: '10px 0' }}>Yükleniyor…</div>
                  ) : detailPuantajlar && detailPuantajlar.length > 0 ? (
                    <table className="machine-table">
                      <thead>
                        <tr><th>Tarih</th><th>Makine</th><th>Operatör</th><th>Süre/Adet</th></tr>
                      </thead>
                      <tbody>
                        {detailPuantajlar.map((p, i) => {
                          const d = pjDate(p);
                          return (
                            <tr key={i}>
                              <td>{d ? formatTrDateOnly(d) : (p.Tarih || '-')}</td>
                              <td>{p['Makine İsmi'] || p.machineName || '-'}</td>
                              <td>{p['Operatör Adı'] || p.operatorName || '-'}</td>
                              <td>{p['Toplam Çalışma Süresi'] || p.totalWorkingTime || p['Puantaj Sayısı'] || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div className="stat-label" style={{ padding: '10px 0' }}>Puantaj kaydı bulunamadı.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAnalytics;
